import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import apiService, {
  formatRupees,
  handleError,
} from "../../Common/ApiCommon/ApiService";
import { Tooltip } from "react-tooltip";
import { Info } from "lucide-react";

const usePaymentHistory = () => {
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const { id } = useParams();
  const [selectedFilter, setSelectedFilter] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const navigate = useNavigate();

  const formatDate = (date) => {
    return new Date(date).toISOString().split("T")[0];
  };

  const getDateRange = (filter) => {
    const today = new Date();
    let fromDate, toDate;

    if (filter === "today") {
      fromDate = toDate = today;
    } else if (filter === "weekly") {
      fromDate = new Date(today);
      fromDate.setDate(today.getDate() - 7);
      toDate = today;
    } else if (filter === "monthly") {
      fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
      toDate = today;
    }

    return {
      fromDate: formatDate(fromDate),
      toDate: formatDate(toDate),
    };
  };

  const fetchPaymentHistory = async () => {
    try {
      let filterQuery = "";
      let from_date = fromDate;
      let to_date = toDate;

      if (
        selectedFilter === "today" ||
        selectedFilter === "weekly" ||
        selectedFilter === "monthly"
      ) {
        const range = getDateRange(selectedFilter);
        from_date = range.fromDate;
        to_date = range.toDate;
      } else if (selectedFilter === "custom" && fromDate && toDate) {
        from_date = formatDate(fromDate);
        to_date = formatDate(toDate);
      }

      filterQuery = `&filter=${selectedFilter}&from_date=${from_date}&to_date=${to_date}`;
      const response = await apiService.get(
        `/payment_histories?customer_id=${id}${filterQuery}`
      );
      setPaymentHistory(response.data);
    } catch (errors) {
      handleError(errors);
      setPaymentHistory([]);
    }
  };

  useEffect(() => {
    if (id) {
      fetchPaymentHistory();
    }
  }, [id, selectedFilter, fromDate, toDate]);

  const columns = useMemo(
    () => [
      {
        Header: "Booking id",
        accessor: "booking_id",
        Cell: ({ value }) => (
          <span
            className="cursor-pointer hover:text-blue hover:underline"
            onClick={() =>
              navigate(`/mainDashboard/booking`, {
                state: {
                  value,
                  showPreviousBooking: "previous",
                  highlightedBookingId: value,
                },
              })
            }
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Date",
        accessor: "end_date",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Mode",
        accessor: "description",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Time",
        accessor: "transaction_time",
        width: "200px",
        Cell: ({ value }) => (
          <div>
            <p className="text-nowrap">{value}</p>
          </div>
        ),
      },
      { Header: "Previous Amt", accessor: "previous_amount" },
      {
        Header: () => (
          <div className="flex items-center gap-2">
            <span>Today Amt</span>
            <div className="relative">
              <Info
                className="w-4 h-4 text-gray-500 cursor-pointer"
                data-tooltip-id="today-amount-info"
              />
              <Tooltip
                id="today-amount-info"
                place="top"
                effect="solid"
                style={{
                  backgroundColor: "#fff",
                  color: "#000",
                  fontSize: "10px",
                  padding: "2px 4px",
                  borderRadius: "12px",
                  fontWeight: "500",
                }}
              >
                <p>Today Booking Amount</p>
              </Tooltip>
            </div>
          </div>
        ),
        accessor: "booking_amount",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: () => (
          <div className="flex items-center gap-2">
            <span>Total Amt</span>
            <div className="relative">
              <Info
                className="w-4 h-4 text-gray-500 cursor-pointer"
                data-tooltip-id="total-amount-info"
              />
              <Tooltip
                id="total-amount-info"
                place="top"
                effect="solid"
                style={{
                  backgroundColor: "#fff",
                  color: "#000",
                  fontSize: "10px",
                  padding: "2px 4px",
                  borderRadius: "12px",
                  fontWeight: "500",
                }}
              >
                <p>Total Amount (Today + Prev)</p>
              </Tooltip>
            </div>
          </div>
        ),
        accessor: "total_amount",
        Cell: ({ value }) => <span>{value}</span>,
      },
      { Header: "Discount", accessor: "discount" },
      {
        Header: "Paid",
        accessor: "paid",
        Cell: ({ value }) => <span className="text-nowrap">{value}</span>,
      },
      {
        Header: "Due",
        accessor: "due",
        Cell: ({ value }) => <span className="text-nowrap">{value}</span>,
      },
          {
        Header: "Remark",
        accessor: "remark",
        Cell: ({ value }) => <span>{value}</span>,
      },
    ],
    []
  );

  const data = useMemo(() => {
    if (!Array.isArray(paymentHistory)) return [];
    return paymentHistory?.map((payment) => ({
      booking_id: payment.booking_id,
      payment_id: payment.id,
      end_date: new Date(payment.end_date || new Date()).toLocaleDateString(
        "en-GB"
      ),
      transaction_time:payment.transaction_time,
      customer: payment.customer,
      description: payment.payment_type || "-",
      start_time: payment.Booking_times?.start_time || "",
      booking_amount: formatRupees(payment.booking_amount),
      total_amount: formatRupees(payment.total_amount),
      discount: formatRupees(payment.discount),
      paid: formatRupees(payment.paid),
      due: formatRupees(payment.due),
      previous_amount: formatRupees(payment.previous_amount),
      remark:payment.remark || "-"
    }));
  }, [paymentHistory]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = data.slice(indexOfFirstRow, indexOfLastRow);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  return {
    columns,
    currentRows,
    totalRows: data.length,
    currentPage,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    selectedFilter,
    setSelectedFilter,
    setFromDate,
    setToDate,
    fromDate,
    toDate,
    data
  };
};

export default usePaymentHistory;
